import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';

import { FolderType, LocationRoleAssignmentSummaryDTO, OrganizationDTO } from '@/api/generated.ts';
import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter.tsx';
import { borders } from '@/theme/constants.ts';
import typography from '@/theme/typography.ts';

import LocationItem from './LocationItem.tsx';
import LocationPathPopper from './LocationPathPopper.tsx';

interface LocationAccessDisplayProps {
  assignments?: LocationRoleAssignmentSummaryDTO[];
  organization?: OrganizationDTO;
  sx?: SxProps<Theme>;
  colorized?: boolean;
  useInsideDialog?: boolean;
  placeholder?: string | React.ReactNode;
  totalCount?: number;
}

const MAX_VISIBLE_ASSIGNMENTS = 3;

const LocationAccessDisplay: React.FC<LocationAccessDisplayProps> = ({
  assignments,
  organization,
  sx,
  colorized,
  placeholder,
  useInsideDialog,
  totalCount
}) => {
  const { t } = useTranslation('processes');

  const visibleAssignments = useMemo(() => assignments?.slice(0, MAX_VISIBLE_ASSIGNMENTS), [assignments]);
  const hiddenItemCounterValue = totalCount
    ? (totalCount ?? 0) - (visibleAssignments?.length ?? 0)
    : (assignments?.length ?? 0) - (visibleAssignments?.length ?? 0);

  if (!assignments?.length) {
    return (
      placeholder || (
        <Typography
          sx={mergeSx<Theme>(
            {
              ...typography.body2,
              color: 'grey.700'
            },
            sx
          )}
        >
          {t('layouts.location.placeholder', { count: 1 })}
        </Typography>
      )
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={mergeSx<Theme>(
          {
            ...typography.body2,
            '& .LocationItem-draft-outer': {
              borderColor: 'primary.500',
              borderStyle: 'dashed'
            },
            '& .LocationItem-draft .LocationItem-icon': {
              color: 'primary.500'
            },
            '& .LocationItem-draft .LocationItem-label': {
              color: 'primary.500'
            }
          },
          useInsideDialog && {
            '& .LocationItem-label': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: 70
            }
          },
          sx
        )}
      >
        <Stack
          key="locations"
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            '& .LocationItem-root': {
              height: 24,
              borderRadius: 40,
              border: borders.default,
              borderColor: 'grey.400',
              py: '1px',
              px: 1
            },
            '& .LocationItem-icon': {
              color: colorized ? 'primary.main' : 'grey.800'
            },
            '& .LocationItem-label': {
              color: colorized ? 'primary.main' : 'text.primary'
            }
          }}
        >
          {(visibleAssignments || []).map(
            assignment =>
              organization && (
                <LocationPathPopper
                  key={assignment.slug}
                  locationSlug={assignment.slug!}
                  organizationSlug={organization.slug!}
                >
                  <LocationItem
                    type={assignment.type as FolderType}
                    name={assignment.name}
                    role={assignment?.role?.name}
                    sx={{
                      paddingInlineStart: 1,
                      paddingInlineEnd: 0.5,
                      '& .LocationItem-label': {
                        maxWidth: 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      },
                      '& .LocationItem-role': {
                        height: 16,
                        lineHeight: 1,
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                </LocationPathPopper>
              )
          )}

          {hiddenItemCounterValue > 0 && (
            <HiddenItemCounter hiddenItemsCount={hiddenItemCounterValue} sx={{ paddingInlineStart: 0 }} />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default LocationAccessDisplay;
